import React, { useEffect, useRef, useState, useContext } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CloseIcon from '@mui/icons-material/Close';
import { invalidUrlError, validateUrl } from '../../../utils/functions';
import { IconButton } from '@mui/material';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';
import Box from '@mui/material/Box';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Context } from '../../../AuthContext';
import { useCategory } from '../../../CategoryContext';
import { openGoogleDrivePicker, GoogleDriveNotConnectedError } from '../../../utils/googlePicker';
import { Link as RouterLink } from 'react-router-dom';

const DEFAULT_TAG_COLOR = '#356AC3';

const UnifiedModal = ({
  open,
  handleClose,
  item,
  type,
  onSave,
  isVertical = false,
  verticalId,
  existingNames = [],
  endpoint,
  extraPayload,
}) => {
  const { user } = useContext(Context);
  const { verticals } = useCategory();
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [link, setLink] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false); // For calendar visibility
  const [associatedVerticalId, setAssociatedVerticalId] = useState('');
  const [tagColor, setTagColor] = useState(DEFAULT_TAG_COLOR);
  const [assignedTo, setAssignedTo] = useState('');
  const [verticalMembers, setVerticalMembers] = useState([]);
  const [importError, setImportError] = useState('');
  const dateInputRef = useRef(null);

  useEffect(() => {
    const fetchMembers = async () => {
      if (!user || !isVertical || !verticalId) {
        setVerticalMembers([]);
        return;
      }
      try {
        const token = await user.getIdToken();
        const res = await fetch(
          `${
            process.env.REACT_APP_FLASK_WEBSERVER
          }get_vertical_members?vertical_id=${encodeURIComponent(verticalId)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const json = await res.json();
        setVerticalMembers(json.data || []);
      } catch (err) {
        console.error('Failed to fetch vertical members:', err);
        setVerticalMembers([]);
      }
    };
    fetchMembers();
  }, [user, isVertical, verticalId]);

  const filteredVerticals = (verticals || []).filter(
    (v) => v.status === 'Joined' || v.status === 'Created'
  );
  const draftVerticals = filteredVerticals.filter(
    (v) => v.status === 'Created' && (v.invited_count || 0) === 0
  );
  const myVerticals = filteredVerticals.filter(
    (v) => v.status === 'Created' && (v.invited_count || 0) > 0
  );
  const joinedVerticals = filteredVerticals.filter((v) => v.status === 'Joined');

  const verticalHeaderStyles = {
    display: 'flex',
    justifyContent: 'flex-start',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '24px',
    color: '#344054',
    backgroundColor: '#FFFFFF',
    borderBottom: '2px solid #344054',
    paddingX: '16px',
  };

  const getVerticalItemStyles = (showDivider) => ({
    borderBottom: showDivider ? '1px solid #EAECF0' : 'none',
    maxWidth: '300px',
    paddingY: '6px',
    marginX: '1px',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '24px',
    color: '#344054',
    '&.Mui-selected': {
      backgroundColor: '#DEEAFF',
      color: '#006ED4',
    },
    '&.Mui-selected:hover': {
      backgroundColor: '#DEEAFF',
    },
  });

  useEffect(() => {
    const handleDateFromDeadline = (deadline) => {
      const taskDate = new Date(deadline);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);

      if (taskDate.toDateString() === today.toDateString()) {
        setSelectedDate('Today');
        setShowCalendar(false);
      } else if (taskDate.toDateString() === tomorrow.toDateString()) {
        setSelectedDate('Tomorrow');
        setShowCalendar(false);
      } else {
        setSelectedDate(taskDate);
        setShowCalendar(true);
      }
    };

    if (item) {
      setTitle(item.title || '');
      setDetails(item.details || '');
      setLink(item.link || '');
      setAssociatedVerticalId(item.associatedVerticalId || '');
      setTagColor(item.tagColor || DEFAULT_TAG_COLOR);
      setAssignedTo(item.assignedTo || '');

      if (type === 'task' && item.deadline) {
        handleDateFromDeadline(item.deadline);
      } else {
        setSelectedDate(null);
        setShowCalendar(false);
      }
    } else {
      resetForm();
    }
  }, [item, type, open]);

  const resetForm = () => {
    setTitle('');
    setDetails('');
    setLink('');
    setSelectedDate(null);
    setShowCalendar(false);
    setAssociatedVerticalId('');
    setTagColor(DEFAULT_TAG_COLOR);
    setAssignedTo('');
  };

  // Parse a "YYYY-MM-DD" string (as produced by <input type="date">) as a local
  // date. Using `new Date(string)` would parse it as UTC midnight, which shifts
  // the day backwards when read back in a timezone behind UTC.
  const parseLocalDate = (value) => {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    return new Date(value);
  };

  const resolveDeadline = () => {
    if (!selectedDate) return null;

    const today = new Date();

    if (selectedDate === 'Today') return today;

    if (selectedDate === 'Tomorrow') {
      today.setDate(today.getDate() + 1);
      return today;
    }

    return parseLocalDate(selectedDate);
  };

  const trimmedTitle = (title || '').trim();
  const oldFolderName = item?.old_name || item?.title || '';
  const folderNameConflict =
    type === 'folder' &&
    !!trimmedTitle &&
    existingNames.some(
      (n) =>
        n.trim().toLowerCase() === trimmedTitle.toLowerCase() &&
        n.trim().toLowerCase() !== oldFolderName.trim().toLowerCase()
    );

  const handleSave = async (e) => {
    e.preventDefault();
    if (folderNameConflict) return;
    const baseItem = {
      title: title || 'Untitled',
    };

    const typeDataMap = {
      note: {
        ...baseItem,
        details,
        isCompleted: false,
        ...(isVertical
          ? {
              vertical_id: verticalId || null,
              ...(item
                ? { assignedBy: item.assignedBy || null }
                : { assignedBy: user?.displayName || user?.email || null }),
            }
          : { associatedVerticalId: associatedVerticalId || null, tagColor }),
      },
      task: {
        ...baseItem,
        details,
        isCompleted: false,
        deadline: resolveDeadline(),
        ...(isVertical
          ? {
              vertical_id: verticalId || null,
              assignedTo: assignedTo || null,
              ...(item
                ? { assignedBy: item.assignedBy || null }
                : { assignedBy: user?.displayName || user?.email || null }),
            }
          : { associatedVerticalId: associatedVerticalId || null, tagColor }),
      },
      link: {
        ...baseItem,
        link: link || '',
        ...(isVertical ? { vertical_id: verticalId || null } : {}),
      },
      announcement: {
        ...baseItem,
        details,
        vertical_id: verticalId || null,
        ...(item
          ? { assignedBy: item.assignedBy || null }
          : { assignedBy: user?.displayName || user?.email || null }),
      },
      folder: {
        folder_name: title || 'Untitled',
        ...(item ? { folder_id: item.folder_id || item._id } : {}),
        ...(isVertical ? { vertical_id: verticalId || null } : {}),
      },
    };

    let updatedItem = { ...typeDataMap[type], ...(extraPayload || {}) };

    try {
      if (!user) return;
      const token = await user.getIdToken();
      const url = `${process.env.REACT_APP_FLASK_WEBSERVER}${
        endpoint || (item ? `edit_${type}?id=${item._id}` : `add_${type}`)
      }`;
      const method = item ? 'PATCH' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedItem),
      });

      if (!item) {
        const result = await response.json();
        updatedItem = {
          ...updatedItem,
          _id: result._id,
          created_at: new Date().toISOString(),
        };
      } else {
        updatedItem = {
          ...updatedItem,
          _id: item._id,
          created_at: item.created_at,
        };
      }
    } catch (error) {
      console.error(`Error saving ${type}:`, error);
    }

    onSave(updatedItem);
    resetForm();
    handleClose();
  };

  const handleDateSelection = (dateType) => {
    if (selectedDate === dateType) {
      // If clicked button is already selected, clear selection
      setSelectedDate(null);
      setShowCalendar(false);
    } else {
      // Otherwise, select the clicked date type
      setSelectedDate(dateType);
      setShowCalendar(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          fontSize: '26px',
          marginBottom: '10px',
          fontWeight: 700,
          textAlign: 'left',
        }}
      >
        <span style={{ textTransform: 'capitalize' }}>{item ? `Edit ${type}` : `Add ${type}`}</span>

        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={() => ({
            position: 'absolute',
            right: 18,
            top: 18,
            color: '#000',
          })}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent style={{ marginTop: 10 }}>
        <form onSubmit={handleSave}>
          <input
            type="text"
            placeholder={type !== 'folder' ? 'Title' : 'Name'}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            disabled={type === 'link' && item?.default === true ? true : false}
            className="input-field"
            style={{
              border: '1px solid #98A2B3',
              width: '100%',
              padding: '10px',
              borderRadius: '5px',
              marginBottom: '10px',
              color: '#000',
              outline: 'none', // prevent double border
              boxShadow: '0 0 0 2px transparent', // remove default
            }}
            required={true}
          />
          {folderNameConflict && (
            <p style={{ color: '#D92D20', fontSize: '13px' }}>
              A folder with this name already exists.
            </p>
          )}

          {type !== 'link' && type !== 'folder' && (
            <textarea
              placeholder="Details"
              className="input-field"
              value={details}
              onChange={(event) => setDetails(event.target.value)}
              cols="30"
              rows="10"
              style={{
                width: '100%',
                borderRadius: '5px',
                fontFamily: 'inherit',
                fontSize: '16px',
                padding: '10px',
                border: `1px solid #98A2B3`,
                outline: 'none', // prevent double border
                boxShadow: '0 0 0 2px transparent', // remove default
              }}
            ></textarea>
          )}

          {type === 'link' && (
            <>
              <p style={{ textAlign: 'left', fontWeight: 700 }}>Manual Add</p>
              <input
                type="text"
                placeholder="Add Link"
                pattern={validateUrl}
                title={invalidUrlError}
                className="input-field"
                value={link}
                onChange={(event) => setLink(event.target.value)}
                style={{
                  border: `1px solid #98A2B3`,
                  width: '100%',
                  padding: '10px',
                  borderRadius: '5px',
                  marginBottom: '10px',
                  color: '#000',
                }}
                required={true}
              />
              <strong>OR</strong>
              <p style={{ textAlign: 'left', fontWeight: 700 }}>Add via Cloud</p>
              <div
                style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: '10px' }}
              >
                <IconButton
                  onClick={async () => {
                    setImportError('');
                    try {
                      await openGoogleDrivePicker({
                        user,
                        onPick: ({ link: pickedLink, name }) => {
                          setLink(pickedLink);
                          if (!title) setTitle(name);
                        },
                      });
                    } catch (err) {
                      if (err instanceof GoogleDriveNotConnectedError) {
                        setImportError('not_connected');
                      } else {
                        console.error('Google Picker error:', err);
                        setImportError('generic');
                      }
                    }
                  }}
                  size="small"
                  title="Import from Google Drive"
                >
                  <img
                    src="/workbench/google-drive.svg"
                    alt="Import from Google Drive"
                    style={{ width: 28, height: 28 }}
                  />
                </IconButton>
                <IconButton
                  onClick={() => {
                    if (!window.Dropbox) {
                      console.error('Dropbox SDK not loaded');
                      return;
                    }
                    window.Dropbox.choose({
                      success: (files) => {
                        const f = files[0];
                        if (!f) return;
                        setLink(f.link);
                        if (!title) setTitle(f.name);
                      },
                      linkType: 'preview',
                      multiselect: false,
                    });
                  }}
                  size="small"
                  title="Import from Dropbox"
                >
                  <img
                    src="/workbench/dropbox.svg"
                    alt="Import from Dropbox"
                    style={{ width: 28, height: 28 }}
                  />
                </IconButton>
              </div>
              {importError === 'not_connected' && (
                <div
                  style={{ textAlign: 'center', color: '#B42318', fontSize: 12, marginBottom: 8 }}
                >
                  Connect Google Drive in{' '}
                  <RouterLink to="/profile/connected-accounts" style={{ color: '#356AC3' }}>
                    Account → My Profile → Connected Accounts
                  </RouterLink>
                  .
                </div>
              )}
              {importError === 'generic' && (
                <div
                  style={{ textAlign: 'center', color: '#B42318', fontSize: 12, marginBottom: 8 }}
                >
                  Could not open Google Drive picker. Please try again.
                </div>
              )}
            </>
          )}

          {type === 'task' && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginTop: '20px',
              }}
            >
              <button
                type="button"
                style={{
                  border: '1px solid',
                  borderColor: selectedDate && selectedDate === 'Today' ? '#356AC3' : '#ccc',
                  backgroundColor: selectedDate && selectedDate === 'Today' ? '#DEEAFF' : 'inherit',
                  cursor: 'pointer',
                  padding: '11px 13px',
                  borderRadius: '5px',
                  fontSize: '16px',
                }}
                onClick={() => handleDateSelection('Today')}
              >
                Today
              </button>
              <button
                type="button"
                style={{
                  border: '1px solid',
                  borderColor: selectedDate === 'Tomorrow' ? '#356AC3' : '#ccc',
                  backgroundColor: selectedDate === 'Tomorrow' ? '#DEEAFF' : 'inherit',
                  cursor: 'pointer',
                  padding: '11px 13px',
                  borderRadius: '5px',
                  fontSize: '16px',
                }}
                onClick={() => handleDateSelection('Tomorrow')}
              >
                Tomorrow
              </button>
              <button
                type="button"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '1px solid',
                  borderColor: showCalendar && selectedDate ? '#356AC3' : '#ccc',
                  backgroundColor: showCalendar && selectedDate ? '#DEEAFF' : 'inherit',
                  cursor: 'pointer',
                  padding: '7px 13px',
                  borderRadius: '5px',
                  fontSize: '16px',
                }}
                onClick={() => {
                  setShowCalendar((prevShowCalendar) => {
                    if (!prevShowCalendar) {
                      setSelectedDate(null);
                      dateInputRef.current?.showPicker(); // optional chaining for safety
                    }
                    return !prevShowCalendar;
                  });
                }}
              >
                <span style={{ marginRight: '8px' }}>
                  <EventOutlinedIcon />
                </span>

                <span>Open Calender</span>
              </button>

              {showCalendar && selectedDate && (
                <p>
                  {parseLocalDate(selectedDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                  })}
                </p>
              )}

              <input
                type="date"
                ref={dateInputRef}
                style={{
                  visibility: 'hidden',
                  position: 'absolute',
                }}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>
          )}

          {!isVertical && (type === 'task' || type === 'note') && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginTop: '15px',
              }}
            >
              <Select
                value={associatedVerticalId}
                onChange={(e) => setAssociatedVerticalId(e.target.value)}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return <span style={{ color: '#000' }}>Associated Vertical</span>;
                  }
                  const v = filteredVerticals.find((x) => x.id === selected);
                  return v ? v.name : '';
                }}
                sx={{
                  height: '48px',
                  minWidth: '220px',
                  fontSize: '16px',
                  color: '#000',
                  borderRadius: '5px',
                  backgroundColor: 'inherit',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ccc',
                    borderWidth: '1px',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ccc',
                    borderWidth: '1px',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ccc',
                    borderWidth: '1px',
                  },
                }}
              >
                <MenuItem value="">
                  <span style={{ color: '#667085' }}>None</span>
                </MenuItem>
                {draftVerticals.length > 0 && (
                  <ListSubheader sx={verticalHeaderStyles}>Private Verticals</ListSubheader>
                )}
                {draftVerticals.map((v, i) => (
                  <MenuItem
                    value={v.id}
                    key={v.id}
                    sx={getVerticalItemStyles(
                      i < draftVerticals.length - 1 ||
                        myVerticals.length > 0 ||
                        joinedVerticals.length > 0
                    )}
                  >
                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.name}</Box>
                  </MenuItem>
                ))}
                {myVerticals.length > 0 && (
                  <ListSubheader sx={verticalHeaderStyles}>My Verticals</ListSubheader>
                )}
                {myVerticals.map((v, i) => (
                  <MenuItem
                    value={v.id}
                    key={v.id}
                    sx={getVerticalItemStyles(
                      i < myVerticals.length - 1 || joinedVerticals.length > 0
                    )}
                  >
                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.name}</Box>
                  </MenuItem>
                ))}
                {joinedVerticals.length > 0 && (
                  <ListSubheader sx={verticalHeaderStyles}>Joined Verticals</ListSubheader>
                )}
                {joinedVerticals.map((v, i) => (
                  <MenuItem
                    value={v.id}
                    key={v.id}
                    sx={getVerticalItemStyles(i < joinedVerticals.length - 1)}
                  >
                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.name}</Box>
                  </MenuItem>
                ))}
              </Select>

              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  border: '1px solid #ccc',
                  borderRadius: '5px',
                  padding: '11px 13px',
                  height: '48px',
                  boxSizing: 'border-box',
                  cursor: 'pointer',
                  backgroundColor: 'inherit',
                  fontSize: '16px',
                  color: '#000',
                }}
                title="Tag color"
              >
                <span
                  style={{
                    display: 'inline-block',
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    backgroundColor: tagColor,
                    border: '1px solid #ccc',
                  }}
                />
                <ArrowDropDownIcon style={{ color: '#000' }} />
                <input
                  type="color"
                  value={tagColor}
                  onChange={(e) => setTagColor(e.target.value)}
                  style={{
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    padding: 0,
                    border: 'none',
                    opacity: 0,
                    pointerEvents: 'none',
                  }}
                />
              </label>
            </div>
          )}

          {isVertical && type === 'task' && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginTop: '15px',
              }}
            >
              <Select
                value={verticalMembers.includes(assignedTo) ? assignedTo : ''}
                onChange={(e) => setAssignedTo(e.target.value)}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return <span style={{ color: '#000' }}>Assign To</span>;
                  }
                  return selected;
                }}
                sx={{
                  height: '48px',
                  minWidth: '220px',
                  fontSize: '16px',
                  color: '#000',
                  borderRadius: '5px',
                  backgroundColor: 'inherit',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ccc',
                    borderWidth: '1px',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ccc',
                    borderWidth: '1px',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ccc',
                    borderWidth: '1px',
                  },
                }}
              >
                <MenuItem value="">
                  <span style={{ color: '#667085' }}>None</span>
                </MenuItem>
                {verticalMembers.map((m, i) => (
                  <MenuItem
                    value={m}
                    key={m}
                    sx={getVerticalItemStyles(i < verticalMembers.length - 1)}
                  >
                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{m}</Box>
                  </MenuItem>
                ))}
              </Select>
            </div>
          )}

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 10,
              marginTop: '15px',
            }}
          >
            <Button
              type="button"
              style={{
                cursor: 'pointer',
                padding: '6px 18px',
                borderRadius: '5px',
                fontSize: '16px',
                border: '1px solid #356AC3',
                textTransform: 'inherit',
                color: '#356AC3',
                fontWeight: 550,
              }}
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={folderNameConflict}
              style={{
                cursor: folderNameConflict ? 'not-allowed' : 'pointer',
                backgroundColor: folderNameConflict ? '#A0A0A0' : '#356AC3',
                color: '#FFF',
                padding: '8px 24px',
                textTransform: 'inherit',
                borderRadius: '5px',
                fontSize: '16px',
                border: 0,
                fontWeight: 550,
              }}
            >
              {item ? `Edit` : `Add`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UnifiedModal;
